import { db, Timestamp } from '@/utils/Firebase'

const COLLECTION = 'aiAlerts'

/**
 * Estructura de un documento en aiAlerts/:
 * {
 *   coachId: string,
 *   studentId: string,
 *   week: string,           // 'YYYY-Www' — ej: '2025-W28'
 *   riskLevel: 'high' | 'medium' | 'low',
 *   summary: string,        // explicación narrativa del riesgo
 *   signals: [              // señales detectadas
 *     { type: 'wellness' | 'adherence', description: string }
 *   ],
 *   read: boolean,
 *   generatedAt: Timestamp,
 * }
 *
 * ID del documento: {coachId}_{studentId}_{week}
 */

export function alertDocId(coachId, studentId, week) {
    return `${coachId}_${studentId}_${week}`
}

export function currentWeek() {
    const now = new Date()
    const startOfYear = new Date(now.getFullYear(), 0, 1)
    const weekNum = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7)
    return `${now.getFullYear()}-W${String(weekNum).padStart(2, '0')}`
}

/**
 * Carga todas las alertas no leídas de un coach.
 * Se usa para el badge en el NavigationDrawer.
 */
export async function loadUnreadAlerts(coachId) {
    if (!coachId) return []
    const snap = await db.collection(COLLECTION)
        .where('coachId', '==', coachId)
        .where('read', '==', false)
        .get()
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

/**
 * Carga la alerta de la semana actual para un alumno.
 * Retorna null si no hay riesgo detectado esta semana.
 */
export async function loadStudentAlert(coachId, studentId, week = null) {
    if (!coachId || !studentId) return null
    const w = week || currentWeek()
    const snap = await db.collection(COLLECTION).doc(alertDocId(coachId, studentId, w)).get()
    if (!snap.exists) return null
    return { id: snap.id, ...snap.data() }
}

/**
 * Carga todas las alertas de un alumno (historial completo).
 */
export async function loadStudentAlertHistory(coachId, studentId) {
    if (!coachId || !studentId) return []
    const snap = await db.collection(COLLECTION)
        .where('coachId', '==', coachId)
        .where('studentId', '==', studentId)
        .orderBy('generatedAt', 'desc')
        .limit(10)
        .get()
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

/**
 * Marca una alerta como leída.
 */
export async function markAlertRead(alertId) {
    if (!alertId) return
    await db.collection(COLLECTION).doc(alertId).update({ read: true })
}

/**
 * SOLO PARA DESARROLLO/TESTING — crea una alerta de prueba.
 * Eliminar o proteger con isDev antes de ir a producción.
 */
export async function createTestAlert(coachId, studentId) {
    if (!coachId || !studentId) return
    const week = currentWeek()
    const docId = alertDocId(coachId, studentId, week)
    await db.collection(COLLECTION).doc(docId).set({
        coachId,
        studentId,
        week,
        riskLevel: 'high',
        summary: 'El alumno muestra señales de riesgo de lesión. En los últimos 7 días registró dolor muscular elevado de forma sostenida y una caída significativa en su adherencia respecto a las semanas anteriores.',
        signals: [
            {
                type: 'wellness',
                description: 'Dolor muscular (muscles_hurt) promedio de 4.2/5 en los últimos 5 registros — por encima del umbral de riesgo (3.5).',
            },
            {
                type: 'wellness',
                description: 'Nivel de energía promedio de 1.8/5 en la última semana — caída del 40% respecto al promedio mensual.',
            },
            {
                type: 'adherence',
                description: 'Solo completó 2 de 5 rutinas asignadas esta semana (40% de adherencia vs 85% histórico).',
            },
        ],
        assumedFields: ['injuries'],
        read: false,
        generatedAt: Timestamp.now(),
    })
}
